import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";

// ─── Sentry client init (J1-CLIENT - Lazy Loaded) ─────────────────────
const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;
const BUILD_ID = RELEASE || "dev";
const BUILD_STATE_KEY = "sloe.build_id";
const APP_STORAGE_PREFIXES = ["sloe.", "sloe_installed_", "paid_"];

// Synchronous early-error buffer before Sentry async load finishes
interface EarlyErrorEvent {
  type: "error" | "unhandledrejection";
  error: any;
  extra?: any;
}
const earlyErrorBuffer: EarlyErrorEvent[] = [];
const MAX_EARLY_ERRORS = 20;

function bufferEarlyError(type: "error" | "unhandledrejection", error: any, extra?: any) {
  if (earlyErrorBuffer.length < MAX_EARLY_ERRORS) {
    earlyErrorBuffer.push({ type, error, extra });
  }
}

const onErrorHandler = (e: ErrorEvent) => {
  bufferEarlyError("error", e.error || e.message, { filename: e.filename, lineno: e.lineno, colno: e.colno });
};

const onUnhandledRejectionHandler = (e: PromiseRejectionEvent) => {
  bufferEarlyError("unhandledrejection", e.reason);
};

if (DSN && typeof window !== "undefined") {
  window.addEventListener("error", onErrorHandler);
  window.addEventListener("unhandledrejection", onUnhandledRejectionHandler);
}

function initLazySentry() {
  if (!DSN || typeof window === "undefined") return;

  const loadSentry = async () => {
    try {
      const { init, setUser, setTag, captureException } = await import("@sentry/react");

      init({
        dsn: DSN,
        release: RELEASE,
        environment: import.meta.env.MODE,
        tracesSampleRate: 0.1,
        ignoreErrors: [
          /rate_limited/,
          /origin_blocked/,
          /invalid_email/,
          /ResizeObserver loop limit exceeded/,
          /ResizeObserver loop completed with undelivered notifications/,
          /Non-Error promise rejection captured/,
          /Network ?Error/i,
          /AbortError/,
        ],
        beforeSend(event) {
          if (event.request?.headers) {
            delete event.request.headers["authorization"];
            delete event.request.headers["cookie"];
          }
          if (event.extra) {
            delete (event.extra as any).email;
            delete (event.extra as any).access_token;
            delete (event.extra as any).refresh_token;
          }
          if (event.user) {
            delete event.user.email;
            delete event.user.username;
          }
          return event;
        },
      });

      // Explicit facade object containing ONLY used methods (no full namespace barrel)
      (window as any).Sentry = {
        captureException,
        setUser,
        setTag,
      };

      window.removeEventListener("error", onErrorHandler);
      window.removeEventListener("unhandledrejection", onUnhandledRejectionHandler);

      // Replay early error buffer
      for (const item of earlyErrorBuffer) {
        captureException(item.error, {
          tags: { buffered: "true", early_type: item.type },
          extra: item.extra,
        });
      }
      earlyErrorBuffer.length = 0;

      console.log(`[sentry-client] lazy initialized release=${RELEASE || "unknown"}`);
    } catch (err) {
      console.warn("[sentry-client] failed to lazy load:", err);
    }
  };

  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(() => void loadSentry(), { timeout: 3000 });
  } else {
    setTimeout(() => void loadSentry(), 1000);
  }
}

async function clearStaleClientStateIfNeeded() {
  if (typeof window === "undefined") return;
  try {
    const previousBuild = window.localStorage.getItem(BUILD_STATE_KEY);
    if (previousBuild === BUILD_ID) return;

    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key) continue;
      if (APP_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));

    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((reg) => reg.unregister()));
    }
    if (window.caches) {
      const names = await window.caches.keys();
      await Promise.all(names.map((name) => window.caches.delete(name)));
    }

    window.localStorage.setItem(BUILD_STATE_KEY, BUILD_ID);
  } catch {
    // Best-effort: if storage is blocked or unavailable, continue booting.
  }
}

// DEV-ONLY: ?__harness=chat mounts the AssistantPage chat harness in isolation
// for layout/pointer debugging. `import.meta.env.DEV` is statically false in a
// production build, so this branch (and the dynamic import) are dropped by the
// bundler — the harness never ships.
const __harness =
  import.meta.env.DEV && typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("__harness")
    : null;

const root = ReactDOM.createRoot(document.getElementById("root")!);

async function bootstrap() {
  await clearStaleClientStateIfNeeded();

  if (__harness === "chat") {
    const Harness = React.lazy(() => import("./dev/AssistantChatHarness"));
    root.render(
      <React.Suspense fallback={null}>
        <Harness />
      </React.Suspense>,
    );
  } else if (__harness === "toast") {
    const ToastHarness = React.lazy(() => import("./dev/UpdateToastHarness"));
    root.render(
      <React.Suspense fallback={null}>
        <ToastHarness />
      </React.Suspense>,
    );
  } else if (__harness === "mc") {
    const MCHarness = React.lazy(() => import("./dev/MissionControlHarness"));
    root.render(
      <React.Suspense fallback={null}>
        <MCHarness />
      </React.Suspense>,
    );
  } else if (__harness === "schema-shell") {
    const SchemaShellHarness = React.lazy(() => import("./dev/SchemaShellHarness"));
    root.render(
      <React.Suspense fallback={null}>
        <SchemaShellHarness />
      </React.Suspense>,
    );
  } else if (__harness === "voice") {
    const VoiceHarness = React.lazy(() => import("./dev/VoiceCallHarness"));
    root.render(
      <React.Suspense fallback={null}>
        <VoiceHarness />
      </React.Suspense>,
    );
  } else {
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>,
    );
  }
  initLazySentry();
}

void bootstrap();
